import { useRef, useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import quinnIcon from '@/assets/quinn-icon.png';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import classroomSyncImage from '@/assets/classroom-sync-modal.png';
import publicFormsImage from '@/assets/public-forms-modal.png';
import quinnAiPreview from '@/assets/quinn-ai-preview.png';
interface FeatureData {
  id: string;
  title: string;
  description: string;
  video?: string;
  image?: string;
  useCases: string[];
}
const featureData: Record<string, FeatureData> = {
  multiview: {
    id: 'multiview',
    title: 'Multi-View Queues',
    description: 'Switch between List, Kanban, Calendar, and Sheet views to match your workflow preferences.',
    video: '/videos/multi-view-queues.mp4',
    useCases: ['Use List view for quick task scanning and updates', 'Switch to Kanban for visual workflow management', 'Plan deadlines with the Calendar view', 'Bulk edit and analyze data in Sheet view']
  },
  documents: {
    id: 'documents',
    title: 'Document Editing',
    description: 'Create and edit documents collaboratively with your team.',
    video: '/videos/document-editing-demo.mp4',
    useCases: ['Create meeting notes and share with your team', 'Build knowledge bases and documentation', 'Collaborate in real-time on project briefs', 'Use templates to standardize workflows']
  },
  apps: {
    id: 'apps',
    title: 'Integrations',
    description: 'Extend queueOS with powerful integrations.',
    video: '/videos/app-store-demo.mp4',
    useCases: ['Sync assignments from Canvas or Google Classroom', 'Create public intake forms for customers', 'Subscribe to calendar feeds for task due dates', 'Customize your workspace with specialized tools']
  },
  ai: {
    id: 'ai',
    title: 'Quinn AI',
    description: 'Let AI automatically organize, prioritize, and categorize your tasks.',
    image: quinnAiPreview,
    useCases: ['Auto-categorize incoming tasks based on content', 'Smart priority suggestions based on deadlines', 'Intelligent task assignment recommendations', 'Workflow optimization suggestions']
  },
  classroomSync: {
    id: 'classroomSync',
    title: 'Classroom Sync',
    description: 'Seamlessly integrate with Canvas LMS and Google Classroom to automatically import assignments as tasks, streamlining assignment organization and student workflows.',
    image: classroomSyncImage,
    useCases: ['Automatically import assignments from Canvas LMS', 'Sync coursework from Google Classroom', 'Track due dates across all your courses', 'Never miss an assignment deadline again']
  },
  publicForms: {
    id: 'publicForms',
    title: 'Public Forms',
    description: 'Create fully customizable public forms for customer support, feedback collection, and data gathering with seamless integration into your workflows.',
    image: publicFormsImage,
    useCases: ['Build customer support intake forms', 'Collect feedback from users and customers', 'Create lead capture forms for your business', 'Automate data collection into your queues']
  }
};
const aiPromptTexts = [
  "Sorting Queue...",
  "Creating Tasks...",
  "Generating Text...",
  "Organizing Sub Tasks...",
  "Syncing from Canvas..."
];

const BentoFeaturesSection = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
  const [aiPromptText, setAiPromptText] = useState(aiPromptTexts[0]);
  // Preload images for modals
  useEffect(() => {
    [classroomSyncImage, publicFormsImage, quinnAiPreview].forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleMouseMove = (e: MouseEvent) => {
      const cards = container.querySelectorAll('.spotlight-wrapper');
      cards.forEach(card => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        (card as HTMLElement).style.setProperty('--mouse-x', `${x}px`);
        (card as HTMLElement).style.setProperty('--mouse-y', `${y}px`);
        // Also set on spotlight-content for the inner glow
        const content = card.querySelector('.spotlight-content') as HTMLElement;
        if (content) {
          content.style.setProperty('--mouse-x', `${x}px`);
          content.style.setProperty('--mouse-y', `${y}px`);
        }
      });
    };
    container.addEventListener('mousemove', handleMouseMove);
    return () => container.removeEventListener('mousemove', handleMouseMove);
  }, []);
  const feature = selectedFeature ? featureData[selectedFeature] : null;
  return <section className="py-24 relative bg-background" ref={containerRef}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-primary via-purple-500 to-primary bg-clip-text text-transparent">Powerful features.</span>
            <br />
            <span className="text-foreground">that's queueOS</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            We stripped away the complexity of traditional project management tools to focus on speed and clarity.
          </p>
        </div>

        {/* Grid Container */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[300px]">
          
          {/* FEATURE 1: Multi-View Queues */}
          <div className="md:col-span-2 spotlight-wrapper rounded-2xl relative group cursor-pointer" onClick={() => setSelectedFeature('multiview')}>
            <div className="spotlight-border rounded-2xl absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
            background: 'radial-gradient(600px circle at var(--mouse-x) var(--mouse-y), rgba(168, 85, 247, 0.3), transparent 40%)'
          }} />
            <div className="spotlight-content rounded-2xl p-8 flex flex-col justify-between overflow-hidden bg-card border border-border group-hover:border-purple-500/50 h-full relative transition-all duration-300 group-hover:shadow-[0_0_30px_rgba(168,85,247,0.15)]">
              <div className="relative z-10">
                {/* Animated Icon: View Switcher */}
                <div className="w-16 h-16 mb-4 relative overflow-hidden">
                  <div className="absolute inset-0 flex animate-[viewCycle_8s_ease-in-out_infinite] [animation-play-state:paused] group-hover:[animation-play-state:running]">
                    {/* List View */}
                    <div className="w-16 h-16 flex-shrink-0 flex flex-col justify-center gap-1.5 p-3 bg-purple-500/10 border border-purple-500/50 rounded-lg">
                      <div className="h-1 w-4/5 bg-purple-400 rounded-full" />
                      <div className="h-1 w-3/5 bg-purple-400 rounded-full" />
                      <div className="h-1 w-4/5 bg-purple-400 rounded-full" />
                    </div>
                    {/* Kanban View */}
                    <div className="w-16 h-16 flex-shrink-0 flex justify-between gap-1 p-3 bg-purple-500/10 border border-purple-500/50 rounded-lg">
                      <div className="w-3 h-full bg-purple-400 rounded" />
                      <div className="w-3 h-3/4 bg-purple-400 rounded" />
                      <div className="w-3 h-full bg-purple-400 rounded" />
                    </div>
                    {/* Calendar View */}
                    <div className="w-16 h-16 flex-shrink-0 flex flex-col justify-center items-center gap-1 p-2 bg-purple-500/10 border border-purple-500/50 rounded-lg">
                      <div className="h-1.5 w-8 bg-purple-400 rounded-full mb-1" />
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-purple-400 rounded-sm" />
                        <div className="w-2 h-2 bg-purple-400 rounded-sm" />
                        <div className="w-2 h-2 bg-purple-400/50 rounded-sm" />
                      </div>
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-purple-400/50 rounded-sm" />
                        <div className="w-2 h-2 bg-purple-400 rounded-sm" />
                        <div className="w-2 h-2 bg-purple-400 rounded-sm" />
                      </div>
                    </div>
                    {/* Sheet View */}
                    <div className="w-16 h-16 flex-shrink-0 grid grid-cols-2 gap-1 p-3 bg-purple-500/10 border border-purple-500/50 rounded-lg">
                      <div className="bg-purple-400 rounded" />
                      <div className="bg-purple-400 rounded" />
                      <div className="bg-purple-400 rounded" />
                      <div className="bg-purple-400 rounded" />
                    </div>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">Multi-View Queues</h3>
                <p className="text-muted-foreground max-w-md">
                  Switch between List, Kanban, Calendar, and Sheet views to match your workflow preferences. See your tasks the way you want.
                </p>
              </div>
              {/* Decorative bg graphic */}
              <div className="absolute right-0 bottom-0 w-2/3 h-2/3 bg-gradient-to-tl from-purple-500/10 to-transparent rounded-tl-full border-t border-l border-border translate-x-10 translate-y-10 group-hover:translate-x-5 group-hover:translate-y-5 transition-transform duration-500" />
              {/* Learn More */}
              <div className="absolute bottom-4 right-4 flex items-center gap-1 text-sm text-purple-500 font-medium opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 z-10">
                <span>Learn more</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* FEATURE 2: Document Editing */}
          <div className="md:col-span-1 spotlight-wrapper rounded-2xl relative group cursor-pointer" onClick={() => setSelectedFeature('documents')}>
            <div className="spotlight-border rounded-2xl absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
            background: 'radial-gradient(600px circle at var(--mouse-x) var(--mouse-y), rgba(168, 85, 247, 0.3), transparent 40%)'
          }} />
            <div className="spotlight-content rounded-2xl p-8 flex flex-col overflow-hidden bg-card border border-border group-hover:border-purple-500/50 h-full relative transition-all duration-300 group-hover:shadow-[0_0_30px_rgba(168,85,247,0.15)]">
              {/* Animated Icon: Typing Document */}
              <div className="w-12 h-14 bg-purple-500/10 border border-purple-500/30 rounded-md p-2 mb-6 group-hover:bg-purple-500/20 transition-colors">
                <div className="space-y-1.5">
                  <div className="h-1 bg-purple-500/30 rounded w-full" />
                  <div className="h-1 bg-purple-500/30 rounded w-full" />
                  <div className="h-1 bg-purple-500/80 rounded w-0 group-hover:w-3/4 transition-all duration-700 ease-out delay-100" />
                  <div className="h-1 bg-purple-500/30 rounded w-0 group-hover:w-1/2 transition-all duration-700 ease-out delay-200" />
                  <div className="h-1 bg-purple-500/30 rounded w-0 group-hover:w-full transition-all duration-700 ease-out delay-300" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Document Editing</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Create and edit documents collaboratively with your team. Rich text editing, templates, and seamless sharing make documentation effortless.
              </p>
              {/* Learn More */}
              <div className="absolute bottom-4 right-4 flex items-center gap-1 text-sm text-purple-500 font-medium opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 z-10">
                <span>Learn more</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* FEATURE 3: Integrations */}
          <div className="md:col-span-1 spotlight-wrapper rounded-2xl relative group cursor-pointer" onClick={() => setSelectedFeature('apps')}>
            <div className="spotlight-border rounded-2xl absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
            background: 'radial-gradient(600px circle at var(--mouse-x) var(--mouse-y), rgba(168, 85, 247, 0.3), transparent 40%)'
          }} />
            <div className="spotlight-content rounded-2xl p-8 flex flex-col overflow-hidden bg-card border border-border group-hover:border-purple-500/50 h-full relative transition-all duration-300 group-hover:shadow-[0_0_30px_rgba(168,85,247,0.15)]">
              {/* Animated Icon: Dragging Apps */}
              <div className="relative w-16 h-16 mb-2">
                <div className="absolute top-0 left-0 w-6 h-6 bg-purple-500/20 border border-purple-500/40 rounded" />
                <div className="absolute top-0 right-4 w-6 h-6 bg-purple-500/20 border border-purple-500/40 rounded" />
                <div className="absolute bottom-4 left-0 w-6 h-6 bg-purple-500/20 border border-purple-500/40 rounded" />
                {/* Draggable App */}
                <div className="absolute bottom-4 right-4 w-6 h-6 bg-purple-500 border border-purple-400 rounded shadow-lg z-10 group-hover:-translate-x-6 group-hover:-translate-y-6 transition-transform duration-500 ease-in-out" />
                {/* Cursor */}
                <svg className="w-4 h-4 text-foreground absolute bottom-0 right-0 z-20 opacity-0 group-hover:opacity-100 group-hover:-translate-x-6 group-hover:-translate-y-6 transition-all duration-500 ease-in-out delay-75 fill-current" viewBox="0 0 24 24">
                  <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Integrations</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Extend queueOS with powerful integrations. Install what you need to customize your workflow.
              </p>
              {/* Learn More */}
              <div className="absolute bottom-4 right-4 flex items-center gap-1 text-sm text-purple-500 font-medium opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 z-10">
                <span>Learn more</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* FEATURE 4: AI Queue Organization */}
          <div className="md:col-span-2 spotlight-wrapper rounded-2xl relative group cursor-pointer" onClick={() => setSelectedFeature('ai')} onMouseEnter={() => setAiPromptText(aiPromptTexts[Math.floor(Math.random() * aiPromptTexts.length)])}>
            <div className="spotlight-border rounded-2xl absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
            background: 'radial-gradient(600px circle at var(--mouse-x) var(--mouse-y), rgba(168, 85, 247, 0.3), transparent 40%)'
          }} />
            <div className="spotlight-content rounded-2xl p-8 flex flex-row items-center justify-between overflow-hidden bg-card border border-border group-hover:border-purple-500/50 h-full relative transition-all duration-300 group-hover:shadow-[0_0_30px_rgba(168,85,247,0.15)]">
              <div className="relative z-10 max-w-lg">
                <div className="flex items-center gap-3 mb-4">
                  {/* Animated Icon: Typing AI Prompt */}
                  <div className="h-8 px-3 rounded bg-purple-500/20 border border-purple-500/40 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                    <div className="text-xs font-mono text-purple-600 overflow-hidden w-0 group-hover:w-32 whitespace-nowrap transition-all duration-1000 ease-linear border-r border-purple-500/50 pr-1">
                      {aiPromptText}
                    </div>
                  </div>
                  <span className="text-[10px] font-bold tracking-wider uppercase text-purple-600 bg-purple-500/20 px-2 py-0.5 rounded border border-purple-500/30">
                    Coming Soon
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">Quinn AI</h3>
                <p className="text-muted-foreground">
                  Let AI automatically organize, prioritize, and categorize your tasks for maximum efficiency. Smart suggestions that learn your workflow.
                </p>
              </div>
              
              {/* Visual Decoration - Quinn Icon */}
              <div className="hidden md:block relative w-32 h-32">
                <div className="absolute inset-0 bg-purple-500/20 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <img src={quinnIcon} alt="AI Assistant" className="relative z-10 w-full h-full object-contain translate-x-16 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-500 ease-out" />
              </div>
              {/* Learn More */}
              <div className="absolute bottom-4 right-4 flex items-center gap-1 text-sm text-purple-500 font-medium opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 z-10">
                <span>Learn more</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* FEATURE 5: Classroom Sync */}
          <div className="md:col-span-2 spotlight-wrapper rounded-2xl relative group cursor-pointer" onClick={() => setSelectedFeature('classroomSync')}>
            <div className="spotlight-border rounded-2xl absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
            background: 'radial-gradient(600px circle at var(--mouse-x) var(--mouse-y), rgba(168, 85, 247, 0.3), transparent 40%)'
          }} />
            <div className="spotlight-content rounded-2xl p-8 flex flex-col justify-between overflow-hidden bg-card border border-border group-hover:border-purple-500/50 h-full relative transition-all duration-300 group-hover:shadow-[0_0_30px_rgba(168,85,247,0.15)]">
              <div className="relative z-10">
                {/* Animated Icon: Stacked Convergence Sync */}
                <div className="w-20 h-20 mb-4 relative">
                  {/* Graduation Cap/LMS Icon (Top-Left) - Solid background to cover bottom icon */}
                  <div className="w-12 h-12 bg-white border border-purple-500/40 rounded-xl flex items-center justify-center absolute top-0 left-0 z-20 group-hover:translate-x-1 group-hover:translate-y-1 group-hover:border-purple-500 transition-all duration-500 overflow-hidden">
                    <div className="absolute inset-0 bg-purple-500/20" />
                    <svg className="w-6 h-6 text-purple-500 relative z-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                      <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
                    </svg>
                  </div>
                  
                  {/* List/Checklist Icon (Bottom-Right) */}
                  <div className="w-12 h-12 bg-purple-500/10 border border-purple-500/40 rounded-xl flex items-center justify-center absolute bottom-0 right-0 z-10 group-hover:-translate-x-1 group-hover:-translate-y-1 group-hover:border-purple-500 transition-all duration-500">
                    <svg className="w-6 h-6 text-purple-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M4 6l2 2 4-4" />
                      <line x1="12" y1="6" x2="20" y2="6" />
                      <path d="M4 12l2 2 4-4" />
                      <line x1="12" y1="12" x2="20" y2="12" />
                      <path d="M4 18l2 2 4-4" />
                      <line x1="12" y1="18" x2="20" y2="18" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">Classroom Sync</h3>
                <p className="text-muted-foreground max-w-md">
                  Seamlessly integrate with Canvas LMS and Google Classroom to automatically import assignments as tasks, streamlining assignment organization and student workflows.
                </p>
              </div>
              {/* Decorative bg graphic */}
              <div className="absolute right-0 bottom-0 w-2/3 h-2/3 bg-gradient-to-tl from-purple-500/10 to-transparent rounded-tl-full border-t border-l border-border translate-x-10 translate-y-10 group-hover:translate-x-5 group-hover:translate-y-5 transition-transform duration-500" />
              {/* Learn More */}
              <div className="absolute bottom-4 right-4 flex items-center gap-1 text-sm text-purple-500 font-medium opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 z-10">
                <span>Learn more</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* FEATURE 6: Public Forms */}
          <div className="md:col-span-1 spotlight-wrapper rounded-2xl relative group cursor-pointer" onClick={() => setSelectedFeature('publicForms')}>
            <div className="spotlight-border rounded-2xl absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
            background: 'radial-gradient(600px circle at var(--mouse-x) var(--mouse-y), rgba(168, 85, 247, 0.3), transparent 40%)'
          }} />
            <div className="spotlight-content rounded-2xl p-8 flex flex-col overflow-hidden bg-card border border-border group-hover:border-purple-500/50 h-full relative transition-all duration-300 group-hover:shadow-[0_0_30px_rgba(168,85,247,0.15)]">
              {/* Animated Icon: Form Builder */}
              <div className="w-14 h-16 bg-purple-500/10 border border-purple-500/30 rounded-md p-2 mb-6 group-hover:bg-purple-500/20 transition-colors relative overflow-hidden">
                {/* Form Fields Animating In */}
                <div className="space-y-2">
                  {/* Text Input */}
                  <div className="h-2 bg-purple-500/40 rounded w-0 group-hover:w-full transition-all duration-300 ease-out" />
                  {/* Checkbox */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-150">
                    <div className="w-2 h-2 border border-purple-500 rounded-sm">
                      <div className="w-full h-full bg-purple-500 scale-0 group-hover:scale-100 transition-transform duration-200 delay-400" />
                    </div>
                    <div className="h-1 w-6 bg-purple-400/50 rounded-full" />
                  </div>
                  {/* Dropdown */}
                  <div className="flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-300">
                    <div className="h-1.5 w-5 bg-purple-400/50 rounded-full" />
                    <svg className="w-2 h-2 text-purple-500 group-hover:rotate-180 transition-transform duration-300 delay-500" viewBox="0 0 12 12" fill="currentColor">
                      <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" />
                    </svg>
                  </div>
                  {/* Submit Button */}
                  <div className="h-2.5 bg-purple-500 rounded w-0 group-hover:w-full transition-all duration-300 ease-out delay-450 mt-1" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Public Forms</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Create fully customizable public forms for customer support, feedback collection, and data gathering with seamless integration into your workflows.
              </p>
              {/* Learn More */}
              <div className="absolute bottom-4 right-4 flex items-center gap-1 text-sm text-purple-500 font-medium opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 z-10">
                <span>Learn more</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Feature Modal */}
      <Dialog open={!!selectedFeature} onOpenChange={open => !open && setSelectedFeature(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{feature?.title}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Video/Image Section */}
            {feature?.video ? <div className="rounded-lg overflow-hidden bg-muted aspect-video">
                <video src={feature.video} className="w-full h-full object-cover" autoPlay loop muted playsInline />
              </div> : feature?.image ? <div className="rounded-lg overflow-hidden bg-muted">
                <img src={feature.image} alt={feature.title} className="w-full h-auto object-cover" />
              </div> : <div className="rounded-lg bg-gradient-to-br from-purple-500/20 to-purple-600/10 aspect-video flex items-center justify-center">
                <div className="text-center">
                  <img src={quinnIcon} alt="AI Assistant" className="w-16 h-16 mx-auto mb-3" />
                  <p className="text-muted-foreground">Coming Soon</p>
                </div>
              </div>}

            {/* Description */}
            <p className="text-muted-foreground">{feature?.description}</p>

            {/* Use Cases */}
            <div>
              <h4 className="font-semibold text-foreground mb-3">Use Cases</h4>
              <ul className="space-y-2">
                {feature?.useCases.map((useCase, index) => <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2 flex-shrink-0" />
                    <span>{useCase}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>;
};
export default BentoFeaturesSection;